/**
 * Managed gitconfig writer — runner-managed credential helper config for
 * Tier-2 mount credentials.
 *
 * The runner exposes `<workspace>/.skaile/managed-gitconfig` to the agent
 * process via `GIT_CONFIG_GLOBAL` (with a fallback `[include]` in
 * `~/.gitconfig`). Each Tier-2 mount writes a tagged block into this file
 * pointing `credential.<urlPrefix>.helper` at the shared `git-credentials`
 * store, plus a single line into the credentials store carrying the actual
 * token. Refresh = atomically rewrite the credential line; the helper picks
 * up the new token on the next git network op.
 *
 * Helpers are keyed by the full sanitized repository URL, not its owner.
 * Collision checks canonicalize repository aliases so one repository still
 * has only one credential-exposing mount.
 *
 * Spec: `_devlog/specs/2026-05-05-git-credential-tiers.md`.
 */

import {
  chmodSync,
  closeSync,
  existsSync,
  mkdirSync,
  openSync,
  readFileSync,
  renameSync,
  utimesSync,
  writeSync,
} from "node:fs";
import { dirname } from "node:path";

// ── Public types ─────────────────────────────────────────────────────────────

/**
 * Thrown when two mounts claim credential scopes for the same canonical
 * repository identity. One mount must own the exposed credential.
 */
export class ManagedGitconfigCollisionError extends Error {
  readonly urlPrefix: string;
  readonly existingMountId: string;
  readonly newMountId: string;

  constructor(urlPrefix: string, existingMountId: string, newMountId: string) {
    super(
      `Mount '${newMountId}' tried to register repository credential scope '${urlPrefix}' ` +
        `but mount '${existingMountId}' already owns the same repository identity. ` +
        `Use one credential-exposing mount per repository or disable exposeAccessToken on one mount.`,
    );
    this.name = "ManagedGitconfigCollisionError";
    this.urlPrefix = urlPrefix;
    this.existingMountId = existingMountId;
    this.newMountId = newMountId;
  }
}

/** Required arguments for {@link writeMountBlock}. */
export interface WriteMountBlockOptions {
  /** Mount declaration id; used as the block tag. */
  mountId: string;
  /**
   * Sanitized repository URL the credential should authenticate, e.g.
   * `https://github.com/org/repository.git`. Trailing slashes are preserved
   * verbatim (git treats them as different scopes).
   */
  urlPrefix: string;
  /**
   * The token to expose. Written to {@link credentialsPath} as
   * `https://x-access-token:<token>@<host>` for the helper to read.
   */
  token: string;
  /** Absolute path to the managed-gitconfig file. */
  gitconfigPath: string;
  /** Absolute path to the git-credentials store. */
  credentialsPath: string;
  /**
   * Optional: when set, the credential block points `helper = !sh <path>` at
   * this script instead of the static `store --file=` form. The script is
   * the wake-mid-401 helper rendered by `renderCredentialHelperScript` —
   * stats the credentials file's mtime, touches a refresh-flag if the cached
   * token is stale, and translates the `store --file=` line format into the
   * helper-output format git expects.
   *
   * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md` Step 9a.
   */
  helperScriptPath?: string;
}

/** Required arguments for {@link writeUnavailableMountBlock}. */
export interface WriteUnavailableMountBlockOptions {
  /** Mount declaration id; used as the block tag. */
  mountId: string;
  /** Repository URL scope the failed mount would have authenticated. */
  urlPrefix: string;
  /** Absolute path to the managed-gitconfig file. */
  gitconfigPath: string;
  /** Absolute path to the git-credentials store. */
  credentialsPath: string;
  /**
   * Absolute path to the rendered "unavailable" helper script — one built with
   * `renderCredentialHelperScript({ unavailableReason })`.
   */
  helperScriptPath: string;
}

/** Required arguments for {@link removeUnavailableMountBlock}. */
export interface RemoveUnavailableMountBlockOptions {
  /** Mount declaration id whose unavailable block should be removed. */
  mountId: string;
  /** Absolute path to the managed-gitconfig file. */
  gitconfigPath: string;
  /**
   * Also remove unavailable aliases for this repository. Git matches helpers
   * by URL, not mount id, so a stale alias must not answer another mount.
   */
  urlPrefix?: string;
}

/** Required arguments for {@link removeMountBlock}. */
export interface RemoveMountBlockOptions {
  /** Mount declaration id whose block should be removed. */
  mountId: string;
  /** Absolute path to the managed-gitconfig file. */
  gitconfigPath: string;
  /** Absolute path to the git-credentials store. */
  credentialsPath: string;
}

/** Required arguments for {@link atomicReplaceCredential}. */
export interface AtomicReplaceCredentialOptions {
  /** Mount id (used to find the matching credential line). */
  mountId: string;
  /** Repository URL scope the credential is keyed by. Same value passed to writeMountBlock. */
  urlPrefix: string;
  /** New token to install. */
  token: string;
  /** Absolute path to the git-credentials store. */
  credentialsPath: string;
}

// ── Block markers ────────────────────────────────────────────────────────────

const blockStart = (mountId: string) => `# skaile-mount: ${mountId}`;
const blockEnd = (mountId: string) => `# skaile-mount: ${mountId} (end)`;
const credentialTag = (mountId: string) => `# skaile-mount: ${mountId}`;

/**
 * Marks a failed mount's helper so it reports the connection error instead of
 * prompting. It carries no token and cannot own a repository scope.
 */
const UNAVAILABLE_MARKER = "# skaile-mount-unavailable";

// ── Helpers ──────────────────────────────────────────────────────────────────

function readFileOrEmpty(path: string): string {
  try {
    return readFileSync(path, "utf-8");
  } catch {
    return "";
  }
}

/**
 * Write a string atomically: tmp file in the same dir, then rename. POSIX
 * rename is atomic on the same filesystem, which is the property we need
 * for refresh-time replacement.
 */
function atomicWrite(path: string, content: string, mode: number): void {
  const dir = dirname(path);
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true, mode: 0o700 });
  }
  const tmp = `${path}.tmp.${process.pid}.${Date.now()}.${Math.random().toString(36).slice(2)}`;
  const fd = openSync(tmp, "w", mode);
  try {
    writeSync(fd, content);
  } finally {
    closeSync(fd);
  }
  // Some filesystems honor O_CREAT mode but ignore later edits; reassert.
  chmodSync(tmp, mode);
  renameSync(tmp, path);
}

/**
 * Extract the `[mountId, content]` block list from a managed-gitconfig file.
 * Anything outside a recognized block is returned as a single anonymous
 * `["", content]` entry to preserve unknown lines on rewrite. This keeps the
 * function idempotent against an empty / hand-edited file.
 */
type GitconfigBlock = { mountId: string; lines: string[] };

/**
 * Append a foreign (non-block) line: extend the trailing foreign block if the
 * last block is one, otherwise open a new foreign block under the special ""
 * id. Keeps foreign content out of any recognized block on rewrite.
 */
function appendForeignLine(blocks: GitconfigBlock[], line: string): void {
  const last = blocks[blocks.length - 1];
  if (last && last.mountId === "") {
    last.lines.push(line);
  } else {
    blocks.push({ mountId: "", lines: [line] });
  }
}

/** Drop trailing foreign blocks that contain only blank lines. */
function trimTrailingEmptyForeignBlock(blocks: GitconfigBlock[]): void {
  while (blocks.length > 0) {
    const last = blocks[blocks.length - 1];
    if (last && last.mountId === "" && last.lines.every((l) => l.trim() === "")) {
      blocks.pop();
    } else {
      break;
    }
  }
}

function parseBlocks(text: string): Array<{ mountId: string; lines: string[] }> {
  const blocks: GitconfigBlock[] = [];
  let current: GitconfigBlock | null = null;

  for (const line of text.split("\n")) {
    const startMatch = line.match(/^# skaile-mount: (\S+)$/);
    const endMatch = line.match(/^# skaile-mount: (\S+) \(end\)$/);

    if (startMatch) {
      if (current) blocks.push(current);
      current = { mountId: startMatch[1] ?? "", lines: [line] };
      continue;
    }
    if (endMatch) {
      if (current) {
        current.lines.push(line);
        blocks.push(current);
        current = null;
      }
      continue;
    }
    if (current) {
      current.lines.push(line);
    } else {
      // Foreign content (user edits, leading blank lines). Keep it out of any
      // block by stashing under the special "" id.
      appendForeignLine(blocks, line);
    }
  }
  if (current) blocks.push(current);

  trimTrailingEmptyForeignBlock(blocks);
  return blocks;
}

function renderBlocks(blocks: Array<{ mountId: string; lines: string[] }>): string {
  if (blocks.length === 0) return "";
  // Join lines, preserving the per-block lines structure. Make sure file ends
  // with a single trailing newline so git's parser is happy.
  const out = blocks.map((b) => b.lines.join("\n")).join("\n");
  return out.endsWith("\n") ? out : `${out}\n`;
}

function buildBlock(
  mountId: string,
  urlPrefix: string,
  credentialsPath: string,
  helperScriptPath?: string,
): string[] {
  // Step 9a: when a helperScriptPath is provided, point at the dynamic
  // wake-mid-401 helper instead of the static `store --file=` form. The
  // helper handles stale-token detection + refresh-flag dance and translates
  // the on-disk format into git's helper-output format. See
  // `credential-helper-script.ts`.
  const helperLine = helperScriptPath
    ? `\thelper = !sh ${helperScriptPath}`
    : `\thelper = store --file=${credentialsPath}`;
  return [blockStart(mountId), `[credential "${urlPrefix}"]`, helperLine, blockEnd(mountId)];
}

function buildCredentialLine(urlPrefix: string, token: string, mountId: string): string {
  // Keep the store host-only: gitconfig selects the repository-scoped helper,
  // then the per-mount script selects this line by its mount tag.
  const u = new URL(urlPrefix);
  const host = u.host;
  const scheme = u.protocol.replace(/:$/, "");
  // Percent-encode the token so an `@`/`:`/`%` in it can't break the helper's
  // `scheme://user:pass@host` parsing; the helper script url-decodes it back.
  const encodedToken = encodeURIComponent(token);
  return `${scheme}://x-access-token:${encodedToken}@${host} ${credentialTag(mountId)}`;
}

function readCredentialLines(path: string): string[] {
  return readFileOrEmpty(path)
    .split("\n")
    .filter((l) => l.length > 0);
}

function writeCredentialLines(path: string, lines: string[]): void {
  const content = lines.length > 0 ? `${lines.join("\n")}\n` : "";
  // Mode 0640: owner (skaile-runner) read+write, group (skaile) read-only.
  // Under UID separation (Step 4 of git-credential-tiers), the runner runs as
  // skaile-runner with primary group skaile so the agent process (uid skaile)
  // can read the credentials store via group membership while only the runner
  // can rewrite it.
  atomicWrite(path, content, 0o640);
}

function findExistingBlock(
  blocks: Array<{ mountId: string; lines: string[] }>,
  mountId: string,
): { mountId: string; lines: string[] } | undefined {
  return blocks.find((b) => b.mountId === mountId);
}

function blockUrlPrefix(block: { lines: string[] }): string | undefined {
  for (const line of block.lines) {
    const m = line.match(/^\[credential "(.+)"\]$/);
    if (m) return m[1];
  }
  return undefined;
}

function repositoryScopeIdentity(scope: string): string {
  try {
    const url = new URL(scope);
    let pathname = url.pathname.replace(/\/+$/, "").replace(/\.git$/i, "");
    if (url.hostname === "github.com") {
      pathname = pathname.toLowerCase();
    }
    return `${url.protocol}//${url.host}${pathname}`;
  } catch {
    return scope;
  }
}

function scopesTargetSameRepository(left: string, right: string): boolean {
  return repositoryScopeIdentity(left) === repositoryScopeIdentity(right);
}

function isUnavailableBlock(block: { lines: string[] }): boolean {
  return block.lines.some((l) => l.trim() === UNAVAILABLE_MARKER);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Write a credential-helper block for `mountId` into the managed-gitconfig
 * file, and (re)write the matching line in the git-credentials store.
 * Idempotent: re-running with the same arguments is a no-op aside from
 * refreshing the on-disk token. Detects collisions where another mount
 * already owns the same `urlPrefix` and throws
 * {@link ManagedGitconfigCollisionError}.
 *
 * Files are created with mode `0640` — owner (skaile-runner) read+write,
 * group (skaile) read-only. See {@link writeCredentialLines} for the
 * UID-separation rationale.
 */
export function writeMountBlock(opts: WriteMountBlockOptions): void {
  const { mountId, urlPrefix, token, gitconfigPath, credentialsPath, helperScriptPath } = opts;

  // ── Gitconfig: insert/replace the tagged block ──────────────────────────
  const text = readFileOrEmpty(gitconfigPath);
  const parsed = parseBlocks(text);

  // Collision check: any *other* mount block declaring the same urlPrefix?
  // An unavailable block holds no credential, so it cannot collide — and a
  // healthy mount must not be blocked by the corpse of a failed one.
  for (const b of parsed) {
    if (b.mountId === "" || b.mountId === mountId) continue;
    if (isUnavailableBlock(b)) continue;
    const otherPrefix = blockUrlPrefix(b);
    if (otherPrefix !== undefined && scopesTargetSameRepository(otherPrefix, urlPrefix)) {
      throw new ManagedGitconfigCollisionError(urlPrefix, b.mountId, mountId);
    }
  }

  // Evict every unavailable block claiming this repository identity. Two
  // matching helper blocks would leave git free to consult the dead one, whose
  // `quit=1` would abort an otherwise-authenticated push.
  const blocks = parsed.filter((b) => {
    const otherPrefix = blockUrlPrefix(b);
    return !(
      isUnavailableBlock(b) &&
      otherPrefix !== undefined &&
      scopesTargetSameRepository(otherPrefix, urlPrefix)
    );
  });

  const newBlock = {
    mountId,
    lines: buildBlock(mountId, urlPrefix, credentialsPath, helperScriptPath),
  };
  const existing = findExistingBlock(blocks, mountId);
  if (existing) {
    existing.lines = newBlock.lines;
  } else {
    blocks.push(newBlock);
  }
  atomicWrite(gitconfigPath, renderBlocks(blocks), 0o640);

  // ── Credentials store: insert/replace the tagged line ───────────────────
  const lines = readCredentialLines(credentialsPath);
  const tag = credentialTag(mountId);
  const filtered = lines.filter((l) => !l.endsWith(tag));
  filtered.push(buildCredentialLine(urlPrefix, token, mountId));
  writeCredentialLines(credentialsPath, filtered);
}

/**
 * Register a credential helper for a mount that **failed to connect**.
 *
 * Without a block, git finds no helper for the host at all and dies with
 * `fatal: could not read Username for '<host>'` — an error that names neither
 * the mount nor the reason, and that arrives only on writes, since reads
 * against a public remote keep succeeding. The block written here points at an
 * "unavailable" helper script (rendered with `unavailableReason`) so git stops
 * with the actual failure text instead.
 *
 * Also drops any credential line still tagged for `mountId`: the mount failed,
 * so a line left over from an earlier session holds a dead token, and serving
 * it would turn an explainable failure back into an opaque 401.
 *
 * A block is written only when no *available* mount already owns `urlPrefix` —
 * a healthy mount is never shadowed by a dead one.
 */
export function writeUnavailableMountBlock(opts: WriteUnavailableMountBlockOptions): void {
  const { mountId, urlPrefix, gitconfigPath, credentialsPath, helperScriptPath } = opts;

  // ── Credentials store: drop this mount's (now dead) line ────────────────
  const lines = readCredentialLines(credentialsPath);
  const tag = credentialTag(mountId);
  const filtered = lines.filter((l) => !l.endsWith(tag));
  if (filtered.length !== lines.length) {
    writeCredentialLines(credentialsPath, filtered);
  }

  // ── Gitconfig: insert/replace the tagged block ──────────────────────────
  const blocks = parseBlocks(readFileOrEmpty(gitconfigPath));

  for (const b of blocks) {
    if (b.mountId === "" || b.mountId === mountId) continue;
    if (isUnavailableBlock(b)) continue;
    const otherPrefix = blockUrlPrefix(b);
    if (otherPrefix !== undefined && scopesTargetSameRepository(otherPrefix, urlPrefix)) return;
  }

  const newLines = [
    blockStart(mountId),
    UNAVAILABLE_MARKER,
    `[credential "${urlPrefix}"]`,
    `\thelper = !sh ${helperScriptPath}`,
    blockEnd(mountId),
  ];
  const existing = findExistingBlock(blocks, mountId);
  if (existing) {
    existing.lines = newLines;
  } else {
    blocks.push({ mountId, lines: newLines });
  }
  atomicWrite(gitconfigPath, renderBlocks(blocks), 0o640);
}

/**
 * Remove unavailable blocks — the one tagged for `mountId`, and (when
 * `urlPrefix` is given) any that declare that prefix under another id.
 *
 * Called on a successful connect so a stale block from a previous failed
 * session cannot make a now-healthy mount report itself as disconnected, and so
 * a sibling mount that failed earlier in the same `connectAll` cannot answer
 * this one's network operations. The marker check is what makes it safe to call
 * unconditionally, including for Tier-1 mounts that write no block of their
 * own: an *available* block is never touched. Missing file / missing block /
 * only normal blocks are all no-ops.
 */
export function removeUnavailableMountBlock(opts: RemoveUnavailableMountBlockOptions): void {
  const { mountId, gitconfigPath, urlPrefix } = opts;
  const text = readFileOrEmpty(gitconfigPath);
  if (text.length === 0) return;
  const blocks = parseBlocks(text);
  const doomed = new Set(
    blocks.filter((b) => {
      if (!isUnavailableBlock(b)) return false;
      if (b.mountId === mountId) return true;
      const otherPrefix = blockUrlPrefix(b);
      return (
        urlPrefix !== undefined &&
        otherPrefix !== undefined &&
        scopesTargetSameRepository(otherPrefix, urlPrefix)
      );
    }),
  );
  if (doomed.size === 0) return;
  atomicWrite(gitconfigPath, renderBlocks(blocks.filter((b) => !doomed.has(b))), 0o640);
}

/**
 * Remove the block tagged for `mountId` from the managed-gitconfig file and
 * the matching credential line. Best-effort: missing files / missing block
 * are not errors.
 */
export function removeMountBlock(opts: RemoveMountBlockOptions): void {
  const { mountId, gitconfigPath, credentialsPath } = opts;

  // ── Gitconfig ──
  const text = readFileOrEmpty(gitconfigPath);
  if (text.length > 0) {
    const blocks = parseBlocks(text).filter((b) => b.mountId !== mountId);
    atomicWrite(gitconfigPath, renderBlocks(blocks), 0o640);
  }

  // ── Credentials ──
  const lines = readCredentialLines(credentialsPath);
  const tag = credentialTag(mountId);
  const filtered = lines.filter((l) => !l.endsWith(tag));
  if (filtered.length !== lines.length) {
    writeCredentialLines(credentialsPath, filtered);
  }
}

/**
 * Write (or refresh) the credential-helper script at `scriptPath` with mode
 * `0755` so git can execute it via `helper = !sh <scriptPath>`. Idempotent —
 * re-rendering with identical opts produces the same script body, so re-runs
 * are safe. Atomic rename so a concurrent git invocation never sees a
 * half-written file.
 *
 * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md` Step 9a.
 */
export function writeHelperScript(scriptPath: string, body: string): void {
  // Mode 0755: owner rwx, group/other rx — git executes the script as the
  // agent uid which is in the skaile group; rx for group is the safer choice
  // than rx for other.
  atomicWrite(scriptPath, body, 0o755);
}

/**
 * Pre-create the helper's refresh-attempt stamp so the agent uid can truncate
 * it later.
 *
 * The helper script stamps this file itself to rate-limit its refresh dance,
 * but it runs as the agent uid while `.skaile/` is owned by skaile-runner — so
 * the agent can only ever truncate a file that already exists and is
 * group-writable. Mode 0660 (owner+group rw) mirrors what the runner does for
 * the refresh-request flag for exactly the same reason.
 *
 * Never truncates an existing stamp: doing so would reset a live cooldown on
 * every reconnect. Best-effort — a caller on the connect-failure path must not
 * be derailed by this, and the helper survives a missing stamp anyway (it just
 * dances on every git operation instead of once per cooldown window).
 */
export function ensureRefreshAttemptStamp(stampPath: string): void {
  if (existsSync(stampPath)) return;
  const dir = dirname(stampPath);
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true, mode: 0o700 });
  }
  const fd = openSync(stampPath, "a", 0o660);
  closeSync(fd);
  // O_CREAT's mode is umask-masked; reassert so the group bit actually lands.
  chmodSync(stampPath, 0o660);
  // Backdate to the epoch. The helper reads this file's mtime as "when the last
  // dance failed", so a stamp created *now* would silence the first dance for a
  // whole cooldown window — the opposite of what pre-creating it is for.
  utimesSync(stampPath, 0, 0);
}

/**
 * Replace just the credential line for `mountId` atomically — refresh path.
 * The gitconfig block (which only points at the helper) does not need to
 * change; the helper re-reads the credentials file on every git network op.
 *
 * Upserts: inserts the line when `mountId` has none yet. An emptied or missing
 * credentials file must be able to self-heal from the refresh path — refusing
 * to insert would strand interactive git until an external reconnect.
 * Other mounts' lines are left untouched.
 */
export function atomicReplaceCredential(opts: AtomicReplaceCredentialOptions): void {
  const { mountId, urlPrefix, token, credentialsPath } = opts;
  const lines = readCredentialLines(credentialsPath);
  const tag = credentialTag(mountId);
  const without = lines.filter((l) => !l.endsWith(tag));
  without.push(buildCredentialLine(urlPrefix, token, mountId));
  writeCredentialLines(credentialsPath, without);
}
